import { useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import {
	AlertDialog,
	AlertDialogBody,
	AlertDialogCloseButton,
	AlertDialogContent,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogOverlay,
	Box,
	Button,
	CheveronSmallLeftOutline,
	DotsHorizontalOutline,
	Flex,
	HStack,
	IconButton,
	Input,
	InputGroup,
	InputLeftElement,
	Menu,
	MenuButton,
	MenuDivider,
	MenuItem,
	MenuList,
	PencilAltOutline,
	PencilOutline,
	SearchAltOutline,
	Text,
	TrashOutline,
	useDisclosure,
	useToast,
	VStack,
	XOutline,
} from '@bonusly/design-system';

import {
	useBizyDeleteConversationMutation,
	useBizyUpdateConversationMutation,
	useBizyUserConversation,
	useBizyUserConversations,
} from '../../queries/bizy-conversations';
import { BizySidePanelHeader } from './bizy-side-panel-header';
import { getPageDisplayName } from './lib/bizy-page-config';
import { generateConversationTitle } from './lib/conversation-title';
import { getCachedTitle, removeCachedTitle, setCachedTitle } from './lib/conversation-title-cache';
import { clearBizySession, loadBizySession } from './lib/session-storage';
import { useBizySidePanelStore } from './store/use-bizy-side-panel-store';
import { useBizyWidgetStore } from './store/use-bizy-widget-store';

interface BizySidePanelHistoryViewProps {
	onBack: () => void;
	onNewConversation: () => void;
}

function formatLastUpdated(date: Date): string {
	return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

export function BizySidePanelHistoryView({ onBack, onNewConversation }: BizySidePanelHistoryViewProps) {
	const { t } = useTranslation('admin_bizy');
	const { track } = useAnalytics();
	const [searchValue, setSearchValue] = useState('');
	const [selectedConversationId, setSelectedConversationId] = useState<string | undefined>();
	const [renamingId, setRenamingId] = useState<string | undefined>();
	const [renameValue, setRenameValue] = useState('');
	const setResume = useBizyWidgetStore((s) => s.setResume);

	const toast = useToast();
	const closePanel = useBizySidePanelStore((state) => state.close);
	const { data: conversationsData, isLoading } = useBizyUserConversations();
	const { data: selectedConversation } = useBizyUserConversation(selectedConversationId);
	const { mutate: updateConversation } = useBizyUpdateConversationMutation();
	const { mutate: deleteConversationBackend } = useBizyDeleteConversationMutation();
	const conversations = conversationsData?.[0] || [];

	useEffect(() => {
		if (!selectedConversation || !selectedConversationId) return;
		setResume(selectedConversationId, selectedConversation.messages);
		onBack();
	}, [onBack, setResume, selectedConversation, selectedConversationId]);

	const getConversationTitle = (conversation: (typeof conversations)[0]) =>
		conversation.conversationTitle || getCachedTitle(conversation.conversationId) || generateConversationTitle(conversation.firstUserMessage);

	const filteredConversations = useMemo(() => {
		if (!searchValue.trim()) return conversations;
		const query = searchValue.toLowerCase();
		return conversations.filter((conversation) => {
			const title = getConversationTitle(conversation);
			return title.toLowerCase().includes(query);
		});
	}, [conversations, searchValue]); // eslint-disable-line react-hooks/exhaustive-deps -- getConversationTitle is stable

	const handleRename = (conversationId: string) => {
		const conversation = conversations.find((c) => c.conversationId === conversationId);
		setRenameValue(conversation ? getConversationTitle(conversation) : '');
		setRenamingId(conversationId);
	};

	const handleFinishRename = () => {
		if (renamingId && renameValue.trim()) {
			setCachedTitle(renamingId, renameValue.trim());
			updateConversation({ conversationId: renamingId, title: renameValue.trim() });
			track(AnalyticsEvent.BizyAdmin.ConversationRenamed, { path: window.location.pathname });
		}
		setRenamingId(undefined);
		setRenameValue('');
	};

	const deleteDialog = useDisclosure();
	const deleteCancelRef = useRef<HTMLButtonElement>(null);
	const [deletingConversationId, setDeletingConversationId] = useState<string | undefined>();

	const handleDeleteClick = (conversationId: string) => {
		setDeletingConversationId(conversationId);
		deleteDialog.onOpen();
	};

	const handleConfirmDelete = () => {
		if (deletingConversationId) {
			deleteConversationBackend(deletingConversationId, {
				onSuccess: () => {
					removeCachedTitle(deletingConversationId);
					const session = loadBizySession();
					if (session?.conversationId === deletingConversationId) {
						clearBizySession();
					}
					setDeletingConversationId(undefined);
					deleteDialog.onClose();
					toast({ title: t('sidePanel.deleteChatSuccess'), status: 'info', duration: 3000 });
					track(AnalyticsEvent.BizyAdmin.ConversationDeleted, { path: window.location.pathname });
				},
				onError: () => {
					setDeletingConversationId(undefined);
					deleteDialog.onClose();
					toast({ title: t('sidePanel.deleteChatError'), status: 'error', duration: 3000 });
				},
			});
		} else {
			setDeletingConversationId(undefined);
			deleteDialog.onClose();
		}
	};

	return (
		<Flex direction="column" h="100%">
			<BizySidePanelHeader
				left={
					<HStack spacing={2}>
						<IconButton
							aria-label="Back"
							icon={<CheveronSmallLeftOutline boxSize={5} />}
							size="sm"
							bg="secondary.bgAlpha"
							color="secondary.fg"
							_hover={{ bg: 'secondary.bgAlphaHover' }}
							onClick={onBack}
						/>
						<Text fontSize="md" fontWeight="semibold" color="text.default">
							{t('sidePanel.historyTitle')}
						</Text>
					</HStack>
				}
				right={
					<IconButton
						aria-label="Close Bizy"
						icon={<XOutline boxSize={4} />}
						size="sm"
						bg="secondary.bgAlpha"
						color="secondary.fg"
						_hover={{ bg: 'secondary.bgAlphaHover' }}
						onClick={closePanel}
					/>
				}
			/>

			<Box px={4} pt={2} pb={3}>
				<InputGroup size="sm">
					<InputLeftElement pointerEvents="none" h="100%" pl={2}>
						<SearchAltOutline boxSize={4} color="text.softest" />
					</InputLeftElement>
					<Input
						value={searchValue}
						onChange={(event) => setSearchValue(event.target.value)}
						placeholder={t('sidePanel.searchPlaceholder')}
						borderRadius="12px"
						bg="bg.canvas"
						borderColor="border.default"
						py={2}
						pr={3}
						h="auto"
					/>
				</InputGroup>
			</Box>

			<Box px={4} pb={3}>
				<Button
					size="sm"
					variant="alpha"
					colorScheme="secondary"
					w="100%"
					leftIcon={<PencilAltOutline boxSize={4} />}
					onClick={onNewConversation}
					borderRadius="10px"
				>
					{t('sidePanel.newChat')}
				</Button>
			</Box>

			<Box flex={1} overflowY="auto" px={3} pb={3}>
				<VStack align="stretch" spacing={0}>
					{isLoading && (
						<Text fontSize="sm" color="text.soft" px={2} py={2}>
							{t('thinking')}
						</Text>
					)}
					{!isLoading &&
						filteredConversations.map((conversation) => {
							const title = getConversationTitle(conversation);
							const isRenaming = renamingId === conversation.conversationId;

							return (
								<Flex
									key={conversation.conversationId}
									align="center"
									w="100%"
									px={2}
									py={2}
									borderRadius="md"
									cursor="pointer"
									_hover={{ bg: 'secondary.bgAlpha' }}
									role="group"
								>
									<Box flex={1} minW={0} onClick={() => setSelectedConversationId(conversation.conversationId)}>
										{isRenaming ? (
											<Input
												value={renameValue}
												onChange={(e) => setRenameValue(e.target.value)}
												onBlur={handleFinishRename}
												onKeyDown={(e) => {
													if (e.key === 'Enter') handleFinishRename();
												}}
												size="sm"
												fontSize="md"
												fontWeight="medium"
												variant="outline"
												px={1}
												h="28px"
												borderRadius="8px"
												borderColor="primary.fg"
												borderWidth="1.5px"
												_focus={{ borderColor: 'primary.fg', boxShadow: 'none' }}
												autoFocus
												onClick={(e) => e.stopPropagation()}
											/>
										) : (
											<Text fontSize="md" fontWeight="medium" color="text.default" noOfLines={1}>
												{title}
											</Text>
										)}
										<Text fontSize="sm" color="text.softest" mt="2px">
											{getPageDisplayName(conversation.pagePath)} · {formatLastUpdated(conversation.lastMessageAt)}
										</Text>
									</Box>
									<Box flexShrink={0}>
										<Menu>
											<MenuButton
												as={IconButton}
												aria-label="More"
												icon={<DotsHorizontalOutline boxSize={4} />}
												variant="ghost"
												size="xs"
												color="text.disabled"
												_groupHover={{ color: 'text.softer' }}
												_hover={{ bg: 'secondary.bgAlphaHover' }}
												onClick={(e: React.MouseEvent) => e.stopPropagation()}
												sx={{ borderRadius: '8px' }}
											/>
											<MenuList zIndex="popover">
												<MenuItem
													icon={<PencilOutline boxSize={5} />}
													onClick={(e) => {
														e.stopPropagation();
														handleRename(conversation.conversationId);
													}}
												>
													{t('sidePanel.renameChat')}
												</MenuItem>
												<MenuDivider />
												<MenuItem
													color="error.fg"
													icon={<TrashOutline boxSize={5} />}
													onClick={(e) => {
														e.stopPropagation();
														handleDeleteClick(conversation.conversationId);
													}}
												>
													{t('sidePanel.deleteChat')}
												</MenuItem>
											</MenuList>
										</Menu>
									</Box>
								</Flex>
							);
						})}
					{!isLoading && filteredConversations.length === 0 && (
						<Text fontSize="sm" color="text.soft" px={2}>
							{t('sidePanel.noHistory')}
						</Text>
					)}
				</VStack>
			</Box>

			<AlertDialog leastDestructiveRef={deleteCancelRef as RefObject<HTMLButtonElement>} isOpen={deleteDialog.isOpen} onClose={deleteDialog.onClose} isCentered>
				<AlertDialogOverlay>
					<AlertDialogContent>
						<AlertDialogHeader fontSize="lg" fontWeight="bold">
							{t('sidePanel.deleteChatTitle')}
						</AlertDialogHeader>
						<AlertDialogCloseButton />
						<AlertDialogBody>
							<Text>{t('sidePanel.deleteChatConfirm')}</Text>
						</AlertDialogBody>
						<AlertDialogFooter gap={3}>
							<Button ref={deleteCancelRef} variant="outline" colorScheme="secondary" onClick={deleteDialog.onClose}>
								{t('sidePanel.cancel')}
							</Button>
							<Button variant="solid" colorScheme="danger" onClick={handleConfirmDelete}>
								{t('sidePanel.deleteChat')}
							</Button>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialogOverlay>
			</AlertDialog>
		</Flex>
	);
}
